/**
 * RTWO Asset & Token Engine — Repositories
 *
 * In-memory stores remain empty until adapters ingest real asset data.
 */

import type {
  AssetId,
  EventId,
  MetadataId,
  PermissionId,
  PolicyId,
  RecordId,
  RuleId,
  RTWOAssetDefinition,
  RTWOAssetEvent,
  RTWOAssetPermission,
  RTWOAssetRegistryRecord,
  RTWOAssetSupplyPolicy,
  RTWOAssetTransferRule,
  RTWOAssetType,
  RTWOTokenMetadata,
} from '../domain-models';

class InMemoryMapRepository<T> {
  private readonly store = new Map<string, T>();

  constructor(private readonly getKey: (entity: T) => string) {}

  findAll(): T[] {
    return Array.from(this.store.values());
  }

  findById(id: string): T | null {
    return this.store.get(id) ?? null;
  }

  save(entity: T): void {
    this.store.set(this.getKey(entity), entity);
  }
}

export class AssetRepository {
  private readonly byId = new InMemoryMapRepository<RTWOAssetDefinition>((asset) => asset.assetId);

  findAll(): RTWOAssetDefinition[] {
    return this.byId.findAll();
  }

  findById(assetId: AssetId): RTWOAssetDefinition | null {
    return this.byId.findById(assetId);
  }

  findByType(assetType: RTWOAssetType): RTWOAssetDefinition[] {
    return this.findAll().filter((asset) => asset.assetType === assetType);
  }

  save(asset: RTWOAssetDefinition): void {
    this.byId.save(asset);
  }
}

export class SupplyPolicyRepository {
  private readonly byId = new InMemoryMapRepository<RTWOAssetSupplyPolicy>(
    (policy) => policy.policyId
  );
  private readonly byAssetId = new Map<AssetId, PolicyId>();

  findAll(): RTWOAssetSupplyPolicy[] {
    return this.byId.findAll();
  }

  findById(policyId: PolicyId): RTWOAssetSupplyPolicy | null {
    return this.byId.findById(policyId);
  }

  findByAssetId(assetId: AssetId): RTWOAssetSupplyPolicy | null {
    const policyId = this.byAssetId.get(assetId);
    if (!policyId) return null;
    return this.byId.findById(policyId);
  }

  save(policy: RTWOAssetSupplyPolicy): void {
    this.byId.save(policy);
    this.byAssetId.set(policy.assetId, policy.policyId);
  }
}

export class PermissionRepository {
  private readonly byId = new InMemoryMapRepository<RTWOAssetPermission>(
    (permission) => permission.permissionId
  );

  findAll(): RTWOAssetPermission[] {
    return this.byId.findAll();
  }

  findById(permissionId: PermissionId): RTWOAssetPermission | null {
    return this.byId.findById(permissionId);
  }

  findByAssetId(assetId: AssetId): RTWOAssetPermission[] {
    return this.findAll().filter((permission) => permission.assetId === assetId);
  }

  save(permission: RTWOAssetPermission): void {
    this.byId.save(permission);
  }
}

export class TransferRuleRepository {
  private readonly byId = new InMemoryMapRepository<RTWOAssetTransferRule>((rule) => rule.ruleId);

  findAll(): RTWOAssetTransferRule[] {
    return this.byId.findAll();
  }

  findById(ruleId: RuleId): RTWOAssetTransferRule | null {
    return this.byId.findById(ruleId);
  }

  findByAssetId(assetId: AssetId): RTWOAssetTransferRule[] {
    return this.findAll().filter((rule) => rule.assetId === assetId);
  }

  save(rule: RTWOAssetTransferRule): void {
    this.byId.save(rule);
  }
}

export class MetadataRepository {
  private readonly byId = new InMemoryMapRepository<RTWOTokenMetadata>(
    (metadata) => metadata.metadataId
  );
  private readonly byAssetId = new Map<AssetId, MetadataId>();

  findAll(): RTWOTokenMetadata[] {
    return this.byId.findAll();
  }

  findById(metadataId: MetadataId): RTWOTokenMetadata | null {
    return this.byId.findById(metadataId);
  }

  findByAssetId(assetId: AssetId): RTWOTokenMetadata | null {
    const metadataId = this.byAssetId.get(assetId);
    if (!metadataId) return null;
    return this.byId.findById(metadataId);
  }

  save(metadata: RTWOTokenMetadata): void {
    this.byId.save(metadata);
    this.byAssetId.set(metadata.assetId, metadata.metadataId);
  }
}

export class AssetEventRepository {
  private readonly byId = new InMemoryMapRepository<RTWOAssetEvent>((event) => event.eventId);

  findAll(): RTWOAssetEvent[] {
    return this.byId.findAll();
  }

  findById(eventId: EventId): RTWOAssetEvent | null {
    return this.byId.findById(eventId);
  }

  findByAssetId(assetId: AssetId): RTWOAssetEvent[] {
    return this.findAll().filter((event) => event.assetId === assetId);
  }

  save(event: RTWOAssetEvent): void {
    this.byId.save(event);
  }
}

export class RegistryRecordRepository {
  private readonly byId = new InMemoryMapRepository<RTWOAssetRegistryRecord>(
    (record) => record.recordId
  );
  private readonly byAssetId = new Map<AssetId, RecordId>();

  findAll(): RTWOAssetRegistryRecord[] {
    return this.byId.findAll();
  }

  findById(recordId: RecordId): RTWOAssetRegistryRecord | null {
    return this.byId.findById(recordId);
  }

  findByAssetId(assetId: AssetId): RTWOAssetRegistryRecord | null {
    const recordId = this.byAssetId.get(assetId);
    if (!recordId) return null;
    return this.byId.findById(recordId);
  }

  save(record: RTWOAssetRegistryRecord): void {
    this.byId.save(record);
    this.byAssetId.set(record.assetId, record.recordId);
  }
}

export interface ASSRepositories {
  assets: AssetRepository;
  supplyPolicies: SupplyPolicyRepository;
  permissions: PermissionRepository;
  transferRules: TransferRuleRepository;
  metadata: MetadataRepository;
  events: AssetEventRepository;
  registryRecords: RegistryRecordRepository;
}

export function createASSRepositories(): ASSRepositories {
  return {
    assets: new AssetRepository(),
    supplyPolicies: new SupplyPolicyRepository(),
    permissions: new PermissionRepository(),
    transferRules: new TransferRuleRepository(),
    metadata: new MetadataRepository(),
    events: new AssetEventRepository(),
    registryRecords: new RegistryRecordRepository(),
  };
}
